/**
 * TableFiltersSelector Component
 *
 * A button styled consistently with TableViewSelector for accessing
 * table filter controls. Opens a dialog when clicked.
 * Supports a disabled state with a "coming soon" tooltip.
 */

import { useState, useRef, useCallback, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ListFilter, X, MoreHorizontal } from "lucide-react"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"


export interface FilterOption {
  key: string
  label: string
  /**
   * Filter kind. "multiselect" (default) shows a searchable option list;
   * "range" shows two numeric From/To inputs.
   */
  type?: "multiselect" | "range"
  /** Available options for a multiselect filter. Ignored for range filters. */
  options?: string[]
  /** Placeholder for the range "from" input (range filters only). */
  fromPlaceholder?: string
  /** Placeholder for the range "to" input (range filters only). */
  toPlaceholder?: string
}

/**
 * A selected numeric range. Either bound may be null to mean "unbounded on
 * that side". A range with both bounds null is treated as inactive.
 */
export interface RangeValue {
  from: number | null
  to: number | null
}

/** Props for the TableFiltersSelector component. */
interface TableFiltersSelectorProps {
  /** Whether the button is disabled. Shows a tooltip when true. */
  disabled?: boolean
  /** Filter definitions with available options for each filter. */
  filterOptions?: FilterOption[]
  /** Callback fired with the selected filters when the user clicks Save. */
  onSave?: (filters: Map<string, Set<string>>, ranges?: Map<string, RangeValue>) => void
  /** Initial multiselect filters to pre-populate (e.g. restored from URL params). */
  initialFilters?: Map<string, Set<string>>
  /** Initial range filters to pre-populate, keyed by filter key. */
  initialRanges?: Map<string, RangeValue>
}



/**
 * Button that opens a filters dialog for table data.
 *
 * When disabled, shows a "coming soon" tooltip on hover.
 * When enabled, clicking opens a dialog for configuring filters.
 *
 * @param props - Component props.
 * @param props.disabled - Whether the button is disabled.
 */
export default function TableFiltersSelector({
  disabled = false,
  filterOptions = [],
  onSave,
  initialFilters,
  initialRanges,
}: TableFiltersSelectorProps): React.JSX.Element {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>(filterOptions[0]?.key ?? "")
  const [searchQuery, setSearchQuery] = useState<string>("")

  // Tab overflow handling: on narrow viewports (e.g. mobile) the tab row can be
  // wider than the dialog. We make it horizontally scrollable and expose an
  // overflow button that lists every category so all tabs remain reachable.
  const [tabsOverflow, setTabsOverflow] = useState<boolean>(false)
  const [overflowMenuOpen, setOverflowMenuOpen] = useState<boolean>(false)
  const tabsResizeObserverRef = useRef<ResizeObserver | null>(null)

  // Refs to each tab button, keyed by filter key, so we can scroll the active
  // one into view (e.g. after picking it from the overflow menu).
  const tabButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Drag-to-scroll state for the tab row. Tracks whether a drag is in
  // progress and where it started so we can compute the delta to scroll.
  const dragState = useRef<{ isDragging: boolean; startX: number; scrollLeft: number }>({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
  })
  // Whether the last mousedown turned into a real drag. Used to suppress the
  // click on the tab button that fires after a drag release.
  const didDragRef = useRef<boolean>(false)

  /**
   * Callback ref for the scrollable tab row. Attaches a ResizeObserver as soon
   * as the node mounts (which is only while the dialog is open, and possibly a
   * frame after it opens) so the overflow measurement runs once layout is
   * available and re-runs whenever the row is resized. A plain effect can miss
   * this because the dialog content mounts asynchronously.
   *
   * @param node - The tab row element, or null when it unmounts.
   */
  const tabsDragCleanupRef = useRef<(() => void) | null>(null)

  const tabsRowRef = useCallback((node: HTMLDivElement | null): void => {
    // Clean up previous node's listeners and observer.
    tabsDragCleanupRef.current?.()
    tabsDragCleanupRef.current = null
    tabsResizeObserverRef.current?.disconnect()
    tabsResizeObserverRef.current = null

    if (node === null) return

    const measure = (): void => {
      setTabsOverflow(node.scrollWidth > node.clientWidth + 1)
    }
    const resizeObserver: ResizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(node)
    tabsResizeObserverRef.current = resizeObserver

    // Drag-to-scroll: lets users drag the tab row horizontally on desktop.
    // We use mouse events (not pointer capture) to avoid interfering with
    // React's synthetic click system. mousemove/mouseup are on the document
    // so dragging outside the row still scrolls correctly.

    const onMouseDown = (e: MouseEvent): void => {
      if (e.button !== 0) return
      dragState.current = { isDragging: true, startX: e.clientX, scrollLeft: node.scrollLeft }
      didDragRef.current = false
      node.style.cursor = "grabbing"
      node.style.userSelect = "none"
    }

    const onMouseMove = (e: MouseEvent): void => {
      if (!dragState.current.isDragging) return
      const delta: number = e.clientX - dragState.current.startX
      // Only mark as a drag — and scroll — past the threshold so a normal
      // click with slight hand tremor still fires.
      if (Math.abs(delta) > 4) {
        didDragRef.current = true
        node.scrollLeft = dragState.current.scrollLeft - delta
      }
    }

    const onMouseUp = (): void => {
      if (!dragState.current.isDragging) return
      dragState.current.isDragging = false
      node.style.cursor = ""
      node.style.userSelect = ""
      // didDragRef resets in the button's onClick after it's consumed.
    }

    node.addEventListener("mousedown", onMouseDown)
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)

    tabsDragCleanupRef.current = () => {
      node.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
      node.style.cursor = ""
      node.style.userSelect = ""
    }
  }, [])

  // Whenever the active tab changes (including selection from the overflow
  // menu), scroll the tab row so the active tab is back in view. `nearest`
  // avoids unnecessary scrolling when it's already fully visible.
  useEffect(() => {
    const activeButton: HTMLButtonElement | undefined = tabButtonRefs.current.get(activeTab)
    activeButton?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
  }, [activeTab])

  // Draft filters: a map from filter key to the set of selected option values
  const [draftFilters, setDraftFilters] = useState<Map<string, Set<string>>>(initialFilters ?? new Map())

  // Saved filters: tracks what was last committed so the dot indicator works
  const [savedFilters, setSavedFilters] = useState<Map<string, Set<string>>>(initialFilters ?? new Map())

  // Draft/saved range filters, mirroring the multiselect state above but for
  // "range"-type filters (filter key -> {from, to}).
  const [draftRanges, setDraftRanges] = useState<Map<string, RangeValue>>(initialRanges ?? new Map())
  const [savedRanges, setSavedRanges] = useState<Map<string, RangeValue>>(initialRanges ?? new Map())

  // Re-seed local state when the parent's `initialFilters` changes.
  //
  // `initialFilters` is only read by the useState initializers above, which run
  // once on mount. But the parent often supplies filters asynchronously — e.g.
  // on a page refresh the active filters are restored from the URL a tick after
  // this component first mounts, so `initialFilters` is empty at mount and
  // non-empty shortly after. Without this sync the table would be filtered
  // (the parent drives the fetch) while the dialog and the active-dot indicator
  // showed nothing.
  //
  // We only re-seed while the dialog is closed. A closed dialog has no unsaved
  // local edits to protect (Save pushes drafts up to the parent via onSave, and
  // the parent feeds them back as `initialFilters`), so this cannot clobber
  // in-progress selections.
  useEffect(() => {
    if (dialogOpen) return
    const seeded: Map<string, Set<string>> = initialFilters ?? new Map()
    setDraftFilters(seeded)
    setSavedFilters(seeded)
    const seededRanges: Map<string, RangeValue> = initialRanges ?? new Map()
    setDraftRanges(seededRanges)
    setSavedRanges(seededRanges)
  }, [initialFilters, initialRanges, dialogOpen])

  /**
   * Whether a range has at least one bound set (i.e. it actually constrains
   * results). A range with both bounds null is treated as inactive.
   *
   * @param range - The range value to test, or undefined.
   * @returns True when either bound is non-null.
   */
  const isRangeActive = (range: RangeValue | undefined): boolean =>
    range != null && (range.from != null || range.to != null)

  /** Count of saved range filters that are actually active. */
  const activeSavedRangeCount: number = Array.from(savedRanges.values()).filter(isRangeActive).length

  /** Whether any filters are currently active (saved) — multiselect or range. */
  const filtersActive = savedFilters.size > 0 || activeSavedRangeCount > 0

  /** The currently active filter option based on the selected tab. */
  const activeFilter = filterOptions.find((f) => f.key === activeTab)

  /** Options filtered by the search query. */
  const filteredOptions = (activeFilter?.options ?? []).filter((option) =>
    option != null && option.toLowerCase().includes(searchQuery.toLowerCase())
  )

  /** All selected draft filter entries as a flat list for display. */
  const selectedEntries: { filterKey: string; filterLabel: string; option: string }[] = []
  draftFilters.forEach((options, key) => {
    const filterDef = filterOptions.find((f) => f.key === key)
    options.forEach((option) => {
      selectedEntries.push({ filterKey: key, filterLabel: filterDef?.label ?? key, option })
    })
  })
  // Add a single chip per active range, rendered as "Label: from–to".
  draftRanges.forEach((range, key) => {
    if (!isRangeActive(range)) return
    const filterDef = filterOptions.find((f) => f.key === key)
    const fromLabel: string = range.from != null ? String(range.from) : "…"
    const toLabel: string = range.to != null ? String(range.to) : "…"
    selectedEntries.push({
      filterKey: key,
      filterLabel: filterDef?.label ?? key,
      option: `${fromLabel}–${toLabel}`,
    })
  })

  /**
   * Handle tab change — reset search when switching tabs.
   *
   * @param key - The key of the tab to activate.
   */
  const handleTabChange = (key: string): void => {
    setActiveTab(key)
    setSearchQuery("")
  }

  /**
   * Toggle an option in the draft filters for the active tab.
   *
   * @param option - The option value to toggle.
   */
  const handleSelectOption = (option: string): void => {
    setDraftFilters((prev) => {
      const next = new Map(prev)
      const current = next.get(activeTab) ?? new Set()
      const updated = new Set(current)
      if (updated.has(option)) {
        updated.delete(option)
      } else {
        updated.add(option)
      }
      if (updated.size === 0) {
        next.delete(activeTab)
      } else {
        next.set(activeTab, updated)
      }
      return next
    })
  }

  /**
   * Remove an option from the draft filters.
   *
   * @param filterKey - The filter key the option belongs to.
   * @param option - The option value to remove.
   */
  const handleRemoveOption = (filterKey: string, option: string): void => {
    setDraftFilters((prev) => {
      const next = new Map(prev)
      const current = next.get(filterKey)
      if (!current) return next
      const updated = new Set(current)
      updated.delete(option)
      if (updated.size === 0) {
        next.delete(filterKey)
      } else {
        next.set(filterKey, updated)
      }
      return next
    })
  }

  /**
   * Clear a range filter entirely (used by its chip's remove button).
   *
   * @param filterKey - The range filter key to clear.
   */
  const handleRemoveRange = (filterKey: string): void => {
    setDraftRanges((prev) => {
      const next = new Map(prev)
      next.delete(filterKey)
      return next
    })
  }

  /**
   * Update one bound of a range filter's draft value.
   *
   * @param filterKey - The range filter key.
   * @param bound - Which bound to set ("from" or "to").
   * @param raw - The raw input string; blank or non-numeric clears that bound.
   */
  const handleRangeChange = (filterKey: string, bound: "from" | "to", raw: string): void => {
    const trimmed: string = raw.trim()
    const parsed: number | null =
      trimmed === "" || !Number.isFinite(Number(trimmed)) ? null : Number(trimmed)
    setDraftRanges((prev) => {
      const next = new Map(prev)
      const current: RangeValue = next.get(filterKey) ?? { from: null, to: null }
      const updated: RangeValue = { ...current, [bound]: parsed }
      if (updated.from == null && updated.to == null) {
        next.delete(filterKey)
      } else {
        next.set(filterKey, updated)
      }
      return next
    })
  }

  /**
   * Check if an option is already selected in the draft for the active tab.
   *
   * @param option - The option value to check.
   * @returns True if the option is selected.
   */
  const isSelected = (option: string): boolean => {
    return draftFilters.get(activeTab)?.has(option) ?? false
  }

  /**
   * The badge count to show on a filter's tab: the number of selected options
   * for a multiselect, or 1/0 for whether a range is active.
   *
   * @param filter - The filter definition.
   * @returns The count to display (0 means no badge).
   */
  const tabSelectionCount = (filter: FilterOption): number => {
    if (filter.type === "range") {
      return isRangeActive(draftRanges.get(filter.key)) ? 1 : 0
    }
    return draftFilters.get(filter.key)?.size ?? 0
  }

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button variant="outline" size="sm" disabled>
              <ListFilter data-icon="inline-start" className="size-4 mr-1.5" />
              Filters
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>This feature coming soon!</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
        <span className="relative">
          <ListFilter data-icon="inline-start" className="size-4 mr-1.5" />
          {filtersActive && (
            <span className="absolute -top-1 -right-1 size-2 rounded-full bg-primary" />
          )}
        </span>
        Filters
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md md:max-w-xl w-full h-[600px] flex flex-col">
          <DialogHeader>
            <DialogTitle>Filters</DialogTitle>
            <DialogDescription>Configure filters for the table data.</DialogDescription>
          </DialogHeader>

          {/* Selected filters */}
          {selectedEntries.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedEntries.map(entry => {
                const isRange: boolean =
                  filterOptions.find(f => f.key === entry.filterKey)?.type === "range"
                return (
                  <span
                    key={`${entry.filterKey}-${entry.option}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent rounded"
                  >
                    {entry.filterLabel}: {entry.option}
                    <button
                      type="button"
                      onClick={() =>
                        isRange
                          ? handleRemoveRange(entry.filterKey)
                          : handleRemoveOption(entry.filterKey, entry.option)
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-stretch gap-1 border-b">
            <div
              ref={tabsRowRef}
              className="flex min-w-0 flex-1 gap-1 overflow-x-auto no-scrollbar"
            >
              {filterOptions.map(filter => {
                const selectionCount: number = tabSelectionCount(filter)
                return (
                  <button
                    key={filter.key}
                    ref={node => {
                      if (node) tabButtonRefs.current.set(filter.key, node)
                      else tabButtonRefs.current.delete(filter.key)
                    }}
                    type="button"
                    onClick={() => {
                      if (didDragRef.current) {
                        didDragRef.current = false
                        return
                      }
                      handleTabChange(filter.key)
                    }}
                    className={`inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-medium border-b-2 transition-colors select-none ${
                      activeTab === filter.key
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {filter.label}
                    {selectionCount > 0 && (
                      <span className="inline-flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none">
                        {selectionCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* Overflow menu — only shown when the tab row is wider than dialog.*/}
            {tabsOverflow && (
              <Popover open={overflowMenuOpen} onOpenChange={setOverflowMenuOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0 self-center"
                    aria-label="More filter categories"
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-48 gap-0 p-1">
                  <ul className="flex flex-col">
                    {filterOptions.map(filter => {
                      const selectionCount: number = tabSelectionCount(filter)
                      return (
                        <li key={filter.key}>
                          <button
                            type="button"
                            onClick={() => {
                              handleTabChange(filter.key)
                              setOverflowMenuOpen(false)
                            }}
                            className={`w-full flex items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm ${
                              activeTab === filter.key
                                ? "bg-primary/10 font-medium text-primary"
                                : "hover:bg-accent"
                            }`}
                          >
                            <span>{filter.label}</span>
                            {selectionCount > 0 && (
                              <span className="inline-flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none shrink-0">
                                {selectionCount}
                              </span>
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {activeFilter?.type === "range" ? (
            // Range filter: two numeric From/To inputs. Either bound optional.
            <div className="flex-1 min-h-0">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  aria-label={`${activeFilter.label} from`}
                  placeholder={activeFilter.fromPlaceholder ?? "From"}
                  value={draftRanges.get(activeFilter.key)?.from ?? ""}
                  onChange={e => handleRangeChange(activeFilter.key, "from", e.target.value)}
                  className="h-8"
                />
                <span className="text-sm text-muted-foreground">–</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  aria-label={`${activeFilter.label} to`}
                  placeholder={activeFilter.toPlaceholder ?? "To"}
                  value={draftRanges.get(activeFilter.key)?.to ?? ""}
                  onChange={e => handleRangeChange(activeFilter.key, "to", e.target.value)}
                  className="h-8"
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Leave a side blank for no bound on that end.
              </p>
            </div>
          ) : (
            <>
              {/* Search */}
              <Input
                type="text"
                placeholder={`Search ${activeFilter?.label ?? ""}...`}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="h-8"
              />

              {/* Options list */}
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                {filteredOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No options found.</p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {filteredOptions.map(option => (
                      <li
                        key={option}
                        onClick={() => handleSelectOption(option)}
                        className={`px-2 py-1.5 text-sm rounded cursor-pointer ${
                          isSelected(option)
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-accent"
                        }`}
                      >
                        {option}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraftFilters(new Map())
                setDraftRanges(new Map())
              }}
              className="mr-auto"
            >
              Clear all filters
            </Button>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setSavedFilters(draftFilters)
                setSavedRanges(draftRanges)
                onSave?.(draftFilters, draftRanges)
                setDialogOpen(false)
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
